import { Box, IconButton, InputAdornment } from '@mui/material';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Logo from '../../../shared/components/common/logo';
import InputWrapper from '../../../shared/formUtilities/useInput';
import TextWrapper from '../../../shared/formUtilities/useTextWrapper';
import ButtonWrapper from '../../../shared/formUtilities/useButton';
import { ResetPasswordSchema } from '../../../shared/validations/schema';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { resetPassword } from '../../../services/authService';
import { Toaster } from '../../../shared/components/common/toaster';
import { buildRegionizeRoute } from '../../../helpers/regionRouteHelper';
import { AppRoutes } from '../../../constants/appRoutes';
import { PasswordValidationPopover } from '../../../shared/components/common/passwordValidationPopover';

export const ResetPassword = () => {
	const [loading, setLoading] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
	const [showErrorPopover, setShowErrorPopover] = useState(false);
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const userId = searchParams.get('userId');
	const rawCode = searchParams.get('code');
	const code = rawCode ? decodeURIComponent(rawCode).split(' ').join('+') : null;

	const onSubmit = (values) => {
		setLoading(true);
		resetPassword({ ...values, userId, code })
			.then(() => {
				setLoading(false);
				setIsSuccess(true);
			})
			.catch((err) => {
				Toaster({ msg: 'Unable to reset password!' });
				setLoading(false);
				setIsSuccess(false);
			});
	};

	return (
		<>
			<div className="flex  flex-col flex-auto items-center sm:justify-center min-w-0 h-full bg-gray">
				<div className="w-full relative flex sm:w-auto sm:rounded-2xl sm:shadow-md sm:bg-card bg-white">
					<div className="w-full sm:py-12 sm:px-12 max-w-[400px] sm:w-[400px] mx-auto sm:mx-0">
						<div className="w-full flex flex-col flex-0 relative">
							<div className="w-24 absolute -top-8 -left-4 mb-5">
								<Logo sx={{ height: 100, width: 100 }} />
							</div>
							<TextWrapper
								classList={`text-2xl font-bold pt-10 !text-center ${
									isSuccess ? '!text-primary' : ''
								}`}
								type="h5"
								text={
									!userId || !code
										? 'Unauthorized Activity Detected'
										: !isSuccess
										? 'Reset your password'
										: 'Password reset successfully'
								}
							/>
						</div>
						{!userId || !code ? (
							<Box className="grid grid-flow-row gap-6 mt-4">
								<ButtonWrapper
									variant={'outlined'}
									onClick={() => {
										navigate(buildRegionizeRoute(AppRoutes.Login));
									}}
									icon={<ArrowBackIcon></ArrowBackIcon>}
									label="Back to login"
									classList="w-full"
									type="button"></ButtonWrapper>
							</Box>
						) : !isSuccess ? (
							<Formik
								initialValues={{
									password: '',
									passwordConfirmation: '',
								}}
								enableReinitialize={true}
								validationSchema={ResetPasswordSchema}
								validateOnMount={true}>
								{(formik) => {
									return (
										<Form className="mt-6">
											<Box className="relative">
												<PasswordValidationPopover
													controllerValue={formik.values.password}
													showErrorPopover={showErrorPopover}
												/>
												<InputWrapper
													key={'password'}
													label="New Password"
													size="small"
													error={
														formik.errors.password && formik.touched.password
													}
													helperText={
														formik.errors.password && formik.touched.password
															? formik.errors.password
															: ''
													}
													variant="outlined"
													fullWidth={true}
													onFocus={() => setShowErrorPopover(true)}
													onBlur={() => setShowErrorPopover(false)}
													InputProps={{
														endAdornment: (
															<InputAdornment position="end">
																<IconButton
																	className="!bg-transparent text-primary"
																	onClick={() => {
																		setShowPassword((prev) => !prev);
																		const value = formik.values.password;
																		formik.setFieldValue('password', '');
																		setTimeout(() => {
																			formik.setFieldValue('password', value);
																		});
																	}}>
																	{!showPassword ? (
																		<VisibilityOffOutlinedIcon className="text-md"></VisibilityOffOutlinedIcon>
																	) : (
																		<VisibilityOutlinedIcon className="text-md"></VisibilityOutlinedIcon>
																	)}
																</IconButton>
															</InputAdornment>
														),
													}}
													type={showPassword ? 'text' : 'password'}
													name="password"></InputWrapper>
											</Box>
											<Box className="mt-4">
												<InputWrapper
													key={'passwordConfirmation'}
													label="Confirm Password"
													size="small"
													error={
														formik.errors.passwordConfirmation &&
														formik.touched.passwordConfirmation
													}
													helperText={
														formik.errors.passwordConfirmation &&
														formik.touched.passwordConfirmation
															? formik.errors.passwordConfirmation
															: ''
													}
													variant="outlined"
													fullWidth={true}
													InputProps={{
														endAdornment: (
															<InputAdornment position="end">
																<IconButton
																	className="!bg-transparent text-primary"
																	onClick={() => {
																		setShowPasswordConfirm((prev) => !prev);
																		const value =
																			formik.values.passwordConfirmation;
																		formik.setFieldValue(
																			'passwordConfirmation',
																			''
																		);
																		setTimeout(() => {
																			formik.setFieldValue(
																				'passwordConfirmation',
																				value
																			);
																		});
																	}}>
																	{!showPasswordConfirm ? (
																		<VisibilityOffOutlinedIcon className="text-md"></VisibilityOffOutlinedIcon>
																	) : (
																		<VisibilityOutlinedIcon className="text-md"></VisibilityOutlinedIcon>
																	)}
																</IconButton>
															</InputAdornment>
														),
													}}
													type={showPasswordConfirm ? 'text' : 'password'}
													name="passwordConfirmation"></InputWrapper>
											</Box>
											<ButtonWrapper
												icon={<CheckIcon></CheckIcon>}
												loading={loading}
												label="Reset Password"
												classList="w-full !mt-8"
												onClick={() => {
													formik.setFieldTouched('password', true);
													formik.setFieldTouched('passwordConfirmation', true);
													if (formik.isValid) {
														onSubmit(formik.values);
													}
												}}></ButtonWrapper>
										</Form>
									);
								}}
							</Formik>
						) : (
							<Box className="grid grid-flow-row gap-6 mt-4">
								<TextWrapper
									type="span"
									classList="caption"
									text="You can now sign in with your new password"></TextWrapper>
								<ButtonWrapper
									variant={'outlined'}
									onClick={() => {
										navigate(buildRegionizeRoute(AppRoutes.Login));
									}}
									icon={<ArrowBackIcon></ArrowBackIcon>}
									label="Back to login"
									classList="w-full"
									type="button"></ButtonWrapper>
							</Box>
						)}
					</div>
				</div>
			</div>
		</>
	);
};
